import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { TableFilterService } from 'app/component-library/table/filtering/table-filter.service';
import { TableAction } from 'app/component-library/table/table-action';
import { TableColumn } from 'app/component-library/table/table-column';
import { TableConfig } from 'app/component-library/table/table-config';
import { TableEditMode } from 'app/component-library/table/table-edit-mode';
import { TableParameters } from 'app/component-library/table/table-parameters';
import { TableRowEditMode } from 'app/component-library/table/table-row-edit-mode';
import { TableSelectionMode } from 'app/component-library/table/table-selection-mode';
import { ItemScenarios } from 'app/shared/enums/item-scenarios';
import { TableActions } from 'app/shared/enums/table-actions';
import { NodeInputDataTypePipe } from 'app/shared/pipes/node-input-data-type.pipe';
import { RunnableLookupItemState } from 'app/viewmodels/runnableLookups/runnable-lookup-item-state';
import { RunnableLookupRow } from 'app/viewmodels/runnableLookups/runnableLookup-row';
import { LazyLoadEvent } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})

export class AdminLookupTableConfigService {
  public readonly rowIndexColumnName = 'rowIndex';
  public readonly columnIndexColumnName = 'columnIndex';

  public scenario: ItemScenarios;

  private defaultTableConfigSettings: any = {
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25],
  };

  public get defaultAttributeLoadEvent(): LazyLoadEvent {
    const event = this.createDefaultLoadEvent();
    event.sortField = this.columnIndexColumnName;
    return event;
  }

  public get defaultValueLoadEvent(): LazyLoadEvent {
    const event = this.createDefaultLoadEvent();
    event.sortField = this.rowIndexColumnName;
    return event;
  }

  private createDefaultLoadEvent(): LazyLoadEvent {
    return {
      filters: {},
      first: 0,
      rows: this.defaultTableConfigSettings.rowsPerPage,
      sortOrder: 1,
    };
  }

  constructor(
    private i18NextPipe: I18NextPipe,
    private tableFilterService: TableFilterService,
  ) { }


  public createAttributeTableConfig(canEdit: boolean = true): TableConfig {
    const config = this.createDefaultTableConfig();
    config.dataKey = 'lookupColumnId';
    config.enableFiltering = false;
    config.lazyLoading = false;
    config.title = this.i18NextPipe.transform('LookupAttributesTableDetails.Title');
    config.itemTypeName = this.i18NextPipe.transform('LookupAttributesTableDetails.ItemTypeName');
    config.columns = this.createTableColumns();

    this.configAttributeTableActions(config, canEdit);
    this.setAttributeTableEditable(config, canEdit);

    return config;
  }

  public createValueTableConfig(canEdit: boolean): TableConfig {
    const config = this.createDefaultTableConfig();
    config.dataKey = 'lookupRowId';

    config.editMode = TableEditMode.Cell;
    config.rowEditMode = TableRowEditMode.Cell;
    config.itemTypeName = this.i18NextPipe.transform('AdminLookupsValueTable.Value');
    config.title = this.i18NextPipe.transform('AdminLookupsValueTable.Values');
    config.startingSortField = this.rowIndexColumnName;

    this.configValueTableActions(config, canEdit);
    this.setValueTableEditable(config, canEdit);

    return config;
  }

  private createDefaultTableConfig() {
    const config = new TableConfig();
    config.selectionMode = TableSelectionMode.Multiple;
    config.enableDelete = false;
    config.enableScrolling = false;
    config.lazyLoading = true;
    config.lazyLoadOnInit = false;
    config.showFilters = false;
    config.showRowHighlightInfoColumn = false;
    config.rowsPerPage = this.defaultTableConfigSettings.rowsPerPage;
    config.rowsPerPageOptions = this.defaultTableConfigSettings.rowsPerPageOptions;
    config.rowHighlightFunction = rowData => this.setRowStyleClass(rowData);
    return config;
  }

  private configAttributeTableActions(config: TableConfig, canEdit: boolean): void {
    config.actions = [];
    const deleteAction = this.createDeleteTableAction(canEdit, this.i18NextPipe.transform('LookupAttributesTableDetails.DeleteAttribute'));
    config.actions.push(deleteAction);
  }

  private createTableColumns(): TableColumn[] {
    const nameCol = new TableColumn(this.i18NextPipe.transform('LookupAttributesTableDetails.IdentifierColumn'), 'identifier');
    const labelCol = new TableColumn(this.i18NextPipe.transform('LookupAttributesTableDetails.LabelColumn'), 'headerText');
    const dataTypeCol = new TableColumn(this.i18NextPipe.transform('LookupAttributesTableDetails.DataTypeColumn'), 'dataType');
    dataTypeCol.formatterPipe = NodeInputDataTypePipe;
    dataTypeCol.formatterPipeArgs = [];
    const displayCol = new TableColumn(this.i18NextPipe.transform('LookupAttributesTableDetails.DisplayColumn'), 'isDisplayColumn');
    const internalUOMCol = new TableColumn(this.i18NextPipe.transform('LookupAttributesTableDetails.InternalUOMColumn'), 'uomName');
    return [nameCol, labelCol, dataTypeCol, displayCol, internalUOMCol];
  }

  private setAttributeTableEditable(tableConfig: TableConfig, canEdit: boolean) {
    tableConfig.enableAdd = canEdit;
    tableConfig.enableEdit = canEdit;
    tableConfig.enableSelectionColumn = canEdit;
  }

  private setRowStyleClass(rowData: RunnableLookupRow): string {
    if (this.scenario === ItemScenarios.Copy) return null;

    return (rowData.state === RunnableLookupItemState.Added || rowData.state === RunnableLookupItemState.Modified)
      ? 'row-default-warning'
      : null;
  }

  public updateTableConfigDeleteAction(config: TableConfig, visible: boolean, enable: boolean) {
    const deleteAction = config.actions.find(x => x.actionId === TableActions.Delete);
    if (!deleteAction) return;
    deleteAction.visible = visible;
    deleteAction.enable = enable;
  }

  public createDeleteTableAction(visible: boolean, title: string) {
    const tableAction = new TableAction();
    tableAction.icon = 'trash';
    tableAction.enable = false;
    tableAction.actionId = TableActions.Delete;
    tableAction.visible = visible;
    tableAction.title = title;
    return tableAction;
  }

  private setValueTableEditable(config: TableConfig, canEdit: boolean) {
    config.enableAdd = canEdit;
    config.enableEdit = canEdit;
    config.enableSelectionColumn = canEdit;
  }

  private configValueTableActions(config: TableConfig, canEdit: boolean): void {
    config.actions = [];
    const deleteAction = this.createDeleteTableAction(canEdit, this.i18NextPipe.transform('AdminLookupsValueTable.DeleteValue'));
    config.actions.push(deleteAction);
  }

  public modifyConfig(config: TableConfig, params: object): void {
    Object.keys(params).forEach(k => {
      config[k] = params[k];
    });
  }

  public setConfigEnabling(config: TableConfig, enabled: boolean): void {
    this.modifyConfig(config, {
      enablePagination: enabled,
      enableHeader: enabled,
    });
  }

  public setReadonly(config: TableConfig) {
    this.modifyConfig(config, {
      showFilters: false,
      enableFiltering: true,
      enableAdd: false,
      enableEdit: false,
      enableDelete: false,
      loading: false,
      enableSelectionColumn: false,
    });
    this.updateTableConfigDeleteAction(config, false, false);
  }

  public setRecordCountConfigParameters(config: TableConfig, tableParams: TableParameters, totalRecords: number) {
    const params = this.createRecordCountConfigParams(tableParams, totalRecords);
    this.modifyConfig(config, params);
  }

  public createRecordCountConfigParams(tableParams: TableParameters, totalRecords: number): object {
    if (totalRecords === 0 && tableParams && (tableParams.filters.length === 0)) {
      return {
        showFilters: false,
        enableFiltering: false,
        totalRecords,
      };
    } else {
      return {
        enableFiltering: true,
        totalRecords,
      };
    }
  }

  public lock(config: TableConfig): void {
    this.setConfigEnabling(config, false);
  }

  public unlock(config: TableConfig): void {
    this.setConfigEnabling(config, true);
  }

  public startLoading(config: TableConfig): void {
    this.modifyConfig(config, { loading: true });
  }

  public finishLoading(config: TableConfig): void {
    this.modifyConfig(config, { loading: false });
  }

  public createTableParameters(tableState: LazyLoadEvent, lookupId: string, columns: TableColumn[]): TableParameters {
    const filters = this.tableFilterService.createFilters(tableState.filters, columns) || [];
    const params = new TableParameters();
    params.selectedItemId = lookupId;
    params.filters = filters;
    params.sortField = tableState.sortField;
    params.sortOrder = tableState.sortOrder;
    params.skip = tableState.first;
    params.take = tableState.rows;
    return params;
  }
}
